import React, { useEffect, useState } from 'react';
import { AnnotatedCodeExample, SimpleCodeExample } from '../../types';
import { generateAnnotatedCode } from '../../utils/codeTemplateParser';

/**
 * Enhanced CodeRenderer with validation and confidence scoring
 * 
 * Progressive improvements based on architecture review:
 * - Validation pipeline: Verify token positions are correct
 * - Confidence scoring: Track reliability of each highlight
 * - Self-healing: Auto-repair when validation fails
 * - Performance tracking: Measure rendering time
 * 
 * Backwards compatible - extends existing CodeRenderer functionality
 */

// Enhanced token type with validation data
interface ValidatedToken {
  text: string;
  className?: string;
  annotation?: any;
  validation?: {
    expected: string;
    actual?: string;
    isValid: boolean;
    confidence: number;
    repairAttempted?: boolean;
  };
}

// Enhanced line type with validation
interface ValidatedLine {
  lineNumber: number;
  tokens: ValidatedToken[];
  fullText: string;
  validationPassed: boolean;
}

// Props with optional validation mode
interface CodeRendererEnhancedProps {
  data: AnnotatedCodeExample;
  className?: string;
  enableValidation?: boolean;
  enableConfidence?: boolean;
  onValidationError?: (errors: ValidationError[]) => void;
}

interface ValidationError {
  line: number;
  token: string;
  expected: string;
  actual: string;
  position: { start: number; end: number };
}

// Confidence calculation based on matching quality
function calculateConfidence(token: ValidatedToken, context: string): number {
  if (!token.annotation) return 1.0;
  
  const expectedText = token.text;
  
  // Base confidence from token type
  let confidence = 0.9;
  
  // Exact match = high confidence
  if (context.includes(expectedText)) {
    confidence = 0.95;
  }
  
  // Check if token appears multiple times (ambiguous)
  const occurrences = (context.match(new RegExp(expectedText, 'g')) || []).length;
  if (occurrences > 1) {
    confidence *= 0.8; // Lower confidence for ambiguous matches
  }
  
  // Compound tokens (e.g., Box::new) are more reliable
  if (expectedText.includes('::')) {
    confidence *= 1.1;
  }
  
  // Keywords and types are more reliable than variables
  if (token.className === 'keyword' || token.className === 'type-keyword') {
    confidence *= 1.05;
  }
  
  return Math.min(confidence, 1.0);
}

// Validation pass: Check if highlighted text matches expected
function validateTokenPositions(
  lines: ValidatedLine[], 
  originalCode: string
): ValidationError[] {
  const errors: ValidationError[] = [];
  const codeLines = originalCode.split('\n');
  
  lines.forEach((line, lineIndex) => {
    const actualLine = codeLines[lineIndex] || '';
    let charPosition = 0;
    
    line.tokens.forEach(token => {
      if (token.annotation) {
        // Find token in actual line
        const tokenStart = actualLine.indexOf(token.text, charPosition);
        
        if (tokenStart === -1) {
          errors.push({
            line: line.lineNumber,
            token: token.text,
            expected: token.text,
            actual: `NOT FOUND in: "${actualLine}"`,
            position: { start: charPosition, end: charPosition }
          });
          
          token.validation = {
            expected: token.text,
            actual: 'NOT FOUND',
            isValid: false,
            confidence: 0
          };
        } else {
          const tokenEnd = tokenStart + token.text.length;
          const actualText = actualLine.slice(tokenStart, tokenEnd);
          
          if (actualText === token.text) {
            token.validation = {
              expected: token.text,
              actual: actualText,
              isValid: true,
              confidence: calculateConfidence(token, actualLine)
            };
          } else {
            errors.push({
              line: line.lineNumber,
              token: token.text,
              expected: token.text,
              actual: actualText,
              position: { start: tokenStart, end: tokenEnd }
            });
            
            token.validation = {
              expected: token.text,
              actual: actualText,
              isValid: false,
              confidence: 0.3
            };
          }
        }
        
        charPosition = tokenStart + token.text.length;
      }
    });
  });
  
  return errors;
}

// Self-healing: Attempt to repair validation errors
function attemptRepair(
  lines: ValidatedLine[],
  errors: ValidationError[]
): ValidatedLine[] {
  // Clone lines to avoid mutation
  const repairedLines = JSON.parse(JSON.stringify(lines)) as ValidatedLine[];
  
  errors.forEach(error => {
    const line = repairedLines.find(l => l.lineNumber === error.line);
    if (!line) return;
    
    const token = line.tokens.find(t => t.text === error.token && t.annotation);
    if (!token) return;
    
    // Attempt repair strategies
    if (error.actual !== 'NOT FOUND') {
      // Strategy 1: Update token text to match actual
      token.text = error.actual;
      token.validation = {
        expected: error.expected,
        actual: error.actual,
        isValid: true,
        confidence: 0.7, // Lower confidence for repaired tokens
        repairAttempted: true
      };
    }
  });
  
  return repairedLines;
}

export const CodeRendererEnhanced: React.FC<CodeRendererEnhancedProps> = ({
  data,
  className = '',
  enableValidation = true,
  enableConfidence = true,
  onValidationError
}) => {
  const [validatedLines, setValidatedLines] = useState<ValidatedLine[]>([]);
  const [renderTime, setRenderTime] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  
  useEffect(() => {
    const startTime = performance.now();
    
    // Convert to validated format
    const lines: ValidatedLine[] = data.lines.map(line => ({
      lineNumber: line.lineNumber,
      tokens: line.tokens,
      fullText: line.tokens.map(t => t.text).join(''),
      validationPassed: true
    }));
    
    if (enableValidation) {
      // Extract original code for validation
      const originalCode = lines.map(l => l.fullText).join('\n');
      
      // Validation pass
      const errors = validateTokenPositions(lines, originalCode);
      
      if (errors.length > 0) {
        console.warn(`Validation errors found: ${errors.length}`, errors);
        
        // Attempt self-healing
        const repaired = attemptRepair(lines, errors);
        setValidatedLines(repaired);
        
        // Re-validate after repair
        const remainingErrors = validateTokenPositions(repaired, originalCode);
        setValidationErrors(remainingErrors);
        
        if (onValidationError && remainingErrors.length > 0) {
          onValidationError(remainingErrors);
        }
      } else {
        setValidatedLines(lines);
        setValidationErrors([]);
      }
    } else {
      setValidatedLines(lines);
    }
    
    const endTime = performance.now();
    setRenderTime(endTime - startTime);
  }, [data, enableValidation, onValidationError]);
  
  const renderToken = (token: ValidatedToken, index: number) => {
    const hasLens = token.annotation?.staticData;
    const validation = token.validation;
    
    // Determine confidence-based styling
    let confidenceClass = '';
    let confidenceIndicator = null;
    
    if (enableConfidence && hasLens && validation) {
      if (validation.confidence < 0.7) {
        confidenceClass = 'opacity-50';
        confidenceIndicator = (
          <span className="text-xs text-yellow-500 ml-1" title="Low confidence">?</span>
        );
      } else if (validation.confidence < 0.9) {
        confidenceClass = 'opacity-75';
      }
      
      if (!validation.isValid) {
        confidenceClass = 'bg-red-100 opacity-75';
        confidenceIndicator = (
          <span className="text-xs text-red-500 ml-1" title="Validation failed">!</span>
        );
      }
    }
    
    // Render with lens data
    if (hasLens) {
      const { surface, concept, paradigm, badge } = token.annotation.staticData;
      return (
        <span
          key={index}
          className={`${token.className || ''} hoverable ${confidenceClass}`}
          data-surface={surface}
          data-concept={concept}
          data-paradigm={paradigm}
          data-badge={badge}
          data-confidence={validation?.confidence || 1}
          data-validated={validation?.isValid ? 'true' : 'false'}
        >
          {token.text}
          {confidenceIndicator}
        </span>
      );
    }
    
    // Regular token
    if (token.className) {
      return (
        <span key={index} className={token.className}>
          {token.text}
        </span>
      );
    }
    
    // Plain text
    return <React.Fragment key={index}>{token.text}</React.Fragment>;
  };
  
  return (
    <div className={`font-mono text-sm space-y-0 ${className}`}>
      {/* Render time indicator for development */}
      {import.meta.env.DEV && (
        <div className="text-xs text-gray-400 mb-2">
          Render time: {renderTime.toFixed(2)}ms
          {validationErrors.length > 0 && (
            <span className="text-red-500 ml-2">
              ({validationErrors.length} validation errors)
            </span>
          )}
        </div>
      )}
      
      {validatedLines.map((line, lineIndex) => (
        <div 
          key={lineIndex} 
          className={`flex items-center hover:bg-gray-800/20 ${
            !line.validationPassed ? 'bg-red-50' : ''
          }`}
        >
          <span className="text-gray-500 w-8 text-right mr-4 select-none">
            {line.lineNumber}
          </span>
          <div className="flex-1">
            {line.tokens.map((token, tokenIndex) => renderToken(token, tokenIndex))}
          </div>
        </div>
      ))}
      
      {/* Validation summary */}
      {enableValidation && validationErrors.length > 0 && (
        <div className="mt-4 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
          <div className="font-semibold text-yellow-800 mb-1">
            ⚠️ Validation Issues Detected
          </div>
          <ul className="text-yellow-700 space-y-1">
            {validationErrors.slice(0, 3).map((error, i) => (
              <li key={i}>
                Line {error.line}: Expected "{error.expected}" but found "{error.actual}"
              </li>
            ))}
            {validationErrors.length > 3 && (
              <li>...and {validationErrors.length - 3} more</li>
            )}
          </ul>
          <button 
            className="mt-2 text-yellow-600 hover:text-yellow-800 font-medium"
            onClick={() => console.log('Full validation report:', validationErrors)}
          >
            View Full Report
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Create enhanced sample with validation data
 */
export function createEnhancedSample(): AnnotatedCodeExample {
  const example: SimpleCodeExample = {
    id: 'validation_test',
    title: 'Enhanced CodeRenderer with Validation',
    description: 'Testing validation and confidence scoring',
    knownLanguage: 'C',
    targetLanguage: 'Rust',
    code: `fn process_data(input: Vec<i32>) -> Vec<i32> {
    input.iter()
        .filter(|&x| x > &0)
        .map(|x| x * 2)
        .collect()
}`,
    lenses: {
      'Vec<i32>': {
        surface: 'int[] → Vec<i32>',
        concept: 'C arrays vs Rust vectors',
        paradigm: 'Rust prevents buffer overflows with Vec',
        badge: 'C'
      },
      '.iter()': {
        surface: 'for loop → .iter()',
        concept: 'C manual iteration vs Rust iterators',
        paradigm: 'Rust functional programming paradigm',
        badge: 'C'
      },
      'collect()': {
        surface: 'malloc + loop → collect()',
        concept: 'C manual allocation vs Rust collection',
        paradigm: 'Rust zero-cost abstractions',
        badge: 'C'
      }
    }
  };
  
  return generateAnnotatedCode(example);
}